import {
  IBookableService,
  IBooking,
  IBookingLegOrder
} from '@pharvio/core/shared';

const VAT: IBookableService['taxClass'] = {
  id: 'tax-vat-75',
  code: 'VAT_STANDARD',
  displayName: 'VAT 7.5%',
  rateBps: 750,
  source: 'platform'
};

const ZERO: IBookableService['taxClass'] = {
  id: 'tax-zero',
  code: 'ZERO_RATED',
  displayName: 'Zero rated',
  rateBps: 0,
  source: 'platform'
};

const now = Date.now();
const iso = (offsetMs: number) => new Date(now + offsetMs).toISOString();
const HOUR = 3_600_000;
const DAY = 24 * HOUR;

export const BOOKABLE_SERVICE_FIXTURES: IBookableService[] = [
  {
    id: 'svc-bp-check',
    name: 'Blood pressure check',
    description: 'A nurse-led reading with a printed record.',
    priceMinor: 200000,
    durationMinutes: 15,
    depositPercent: 0,
    taxClass: ZERO,
    isActive: true,
    sellableOnline: true,
    displayOrder: 1,
    branchIds: [],
    createdBy: { kind: 'user', name: 'Ada Obi' },
    createdAt: iso(-30 * DAY),
    updatedAt: iso(-30 * DAY)
  },
  {
    id: 'svc-med-review',
    name: 'Medication review',
    description: 'A 30-minute sit-down with the superintendent pharmacist.',
    priceMinor: 500000,
    durationMinutes: 30,
    depositPercent: 50,
    taxClass: VAT,
    isActive: true,
    sellableOnline: true,
    displayOrder: 2,
    branchIds: ['branch-1'],
    createdBy: { kind: 'user', name: 'Ada Obi' },
    createdAt: iso(-20 * DAY),
    updatedAt: iso(-2 * DAY)
  },
  {
    id: 'svc-vaccination',
    name: 'Travel vaccination',
    description: null,
    priceMinor: 1500000,
    durationMinutes: 45,
    depositPercent: 100,
    taxClass: VAT,
    isActive: true,
    // Staff can still book it; it just isn't offered online.
    sellableOnline: false,
    displayOrder: 3,
    branchIds: [],
    createdBy: { kind: 'user', name: 'Ada Obi' },
    createdAt: iso(-10 * DAY),
    updatedAt: iso(-10 * DAY)
  }
];

const depositOrder = (
  id: string,
  totalMinor: number,
  paid: boolean
): IBookingLegOrder => ({
  id,
  orderNumber: `ORD-${id.slice(-6).toUpperCase()}`,
  leg: 'deposit',
  // A settled leg goes straight to `completed` — there is no fulfilment flow.
  status: paid ? 'completed' : 'pending_payment',
  source: 'staff',
  totalMinor,
  serviceFeeTotalMinor: 0,
  currency: 'NGN',
  reference: paid ? `ps_${id}` : null,
  paymentLinkToken: null,
  paymentLinkExpiresAt: null,
  paymentReceivedAt: paid ? iso(-HOUR) : null,
  invoiceId: paid ? `inv-${id}` : null,
  invoiceNumber: paid ? `INV-2026-0042` : null
});

export const BOOKING_FIXTURES: IBooking[] = [
  {
    id: 'bkg-1',
    bookingNumber: 'BKG-202610-000001',
    status: 'requested',
    source: 'storefront',
    branchId: 'branch-1',
    branchName: 'Ikeja',
    serviceId: 'svc-bp-check',
    serviceName: 'Blood pressure check',
    durationMinutes: 15,
    requestedAt: iso(2 * DAY),
    proposedAt: null,
    scheduledAt: null,
    depositPercent: 0,
    depositAmountMinor: 0,
    balanceAmountMinor: 200000,
    totalMinor: 200000,
    currency: 'NGN',
    depositPaidAt: null,
    balancePaidAt: null,
    depositExpiresAt: null,
    depositOrder: null,
    balanceOrder: null,
    customerNotes: 'Mornings work best.',
    declineReason: null,
    cancellationReason: null,
    confirmedAt: null,
    completedAt: null,
    cancelledAt: null,
    createdAt: iso(-HOUR),
    updatedAt: iso(-HOUR),
    customer: {
      id: 'cust-1',
      name: 'Chidi Eze',
      email: 'chidi@example.com',
      phone: '+2348012345678'
    },
    staffNotes: null,
    servicePriceMinor: 200000,
    startedAt: null,
    deliveredAt: null,
    noShowAt: null,
    expiredAt: null,
    cancelledBy: null,
    createdBy: { kind: 'customer', name: 'Chidi Eze' }
  },
  {
    id: 'bkg-2',
    bookingNumber: 'BKG-202610-000002',
    status: 'confirmed',
    source: 'staff',
    branchId: 'branch-1',
    branchName: 'Ikeja',
    serviceId: 'svc-med-review',
    serviceName: 'Medication review',
    durationMinutes: 30,
    requestedAt: iso(DAY),
    proposedAt: null,
    scheduledAt: iso(DAY),
    depositPercent: 50,
    depositAmountMinor: 268750,
    balanceAmountMinor: 268750,
    totalMinor: 537500,
    currency: 'NGN',
    depositPaidAt: iso(-HOUR),
    balancePaidAt: null,
    depositExpiresAt: null,
    depositOrder: depositOrder('bkg-2-dep', 268750, true),
    balanceOrder: null,
    customerNotes: null,
    declineReason: null,
    cancellationReason: null,
    confirmedAt: iso(-HOUR),
    completedAt: null,
    cancelledAt: null,
    createdAt: iso(-2 * HOUR),
    updatedAt: iso(-HOUR),
    customer: {
      id: 'cust-2',
      name: 'Amina Bello',
      email: null,
      phone: '+2348098765432'
    },
    staffNotes: 'Bring the old prescription.',
    servicePriceMinor: 500000,
    startedAt: null,
    deliveredAt: null,
    noShowAt: null,
    expiredAt: null,
    cancelledBy: null,
    createdBy: { kind: 'user', name: 'Ada Obi' }
  },
  {
    id: 'bkg-3',
    bookingNumber: 'BKG-202610-000003',
    // Delivered but not paid for — "awaiting balance", not "done".
    status: 'pending_balance',
    source: 'staff',
    branchId: 'branch-2',
    branchName: 'Lekki',
    serviceId: 'svc-med-review',
    serviceName: 'Medication review',
    durationMinutes: 30,
    requestedAt: iso(-3 * HOUR),
    proposedAt: null,
    scheduledAt: iso(-3 * HOUR),
    depositPercent: 50,
    depositAmountMinor: 268750,
    balanceAmountMinor: 268750,
    totalMinor: 537500,
    currency: 'NGN',
    depositPaidAt: iso(-4 * HOUR),
    balancePaidAt: null,
    depositExpiresAt: null,
    depositOrder: depositOrder('bkg-3-dep', 268750, true),
    balanceOrder: {
      ...depositOrder('bkg-3-bal', 268750, false),
      leg: 'balance'
    },
    customerNotes: null,
    declineReason: null,
    cancellationReason: null,
    confirmedAt: iso(-5 * HOUR),
    completedAt: null,
    cancelledAt: null,
    createdAt: iso(-6 * HOUR),
    updatedAt: iso(-2 * HOUR),
    customer: {
      id: 'cust-3',
      name: 'Tunde Alabi',
      email: 'tunde@example.com',
      phone: '+2347011122233'
    },
    staffNotes: null,
    servicePriceMinor: 500000,
    startedAt: iso(-3 * HOUR),
    deliveredAt: iso(-2 * HOUR),
    noShowAt: null,
    expiredAt: null,
    cancelledBy: null,
    createdBy: { kind: 'user', name: 'Ada Obi' }
  }
];
